<script setup lang="ts">
import type { MaterialV1 } from '@ilteducation/content-registry-types';
import MaterialResults from '@/components/material/MaterialResults.vue';
import { computed } from 'vue';
import { ContentWithDuplicates, groupContentByOriginalTitle } from '@ilteducation/content';
import { useMaterialsForVideoSearch } from '@/composables/use-material-for-video-search';

const props = defineProps<{
  videoId: string;
  categoryId: string;
  areaId: string;
}>();

const videoId = computed(() => props.videoId);
const categoryId = computed(() => props.categoryId);
const areaId = computed(() => props.areaId);
const { result, isLoading, next, hasMore } = useMaterialsForVideoSearch(videoId, { categoryId, areaId });

const sortBySwedishFirst = (a: ContentWithDuplicates, b: ContentWithDuplicates) => {
  if ((a as MaterialV1).language === 'sv') {
    return -1;
  }
  if ((b as MaterialV1).language === 'sv') {
    return 1;
  }
  return 0;
};

/*
 * Decide sort priority for a material based on type
 * Priority 1: Materials with contentLinks (linked to a specific video) eg. Squirrel video - Squirrel material
 * Priority 2: Materials with refdataLinks containing type "Area" eg. Animals
 * Priority 3: Materials with refdataLinks containing type "Category" eg. Biology
 */
const getMaterialSortPriority = (material: MaterialV1): number => {
  // Priority 1
  if (material.contentLinks && Array.isArray(material.contentLinks) && material.contentLinks.length > 0) {
    return 1;
  }

  // Priority 2
  if (
    material.refdataLinks &&
    Array.isArray(material.refdataLinks) &&
    material.refdataLinks.some((link) => link.type === 'Area')
  ) {
    return 2;
  }

  // Priority 3
  return 3;
};

const deduplicatedMaterials = computed(() => {
  const grouped = groupContentByOriginalTitle([...result.value], sortBySwedishFirst) as MaterialV1[];
  return grouped.sort((a, b) => getMaterialSortPriority(a) - getMaterialSortPriority(b));
});
</script>

<template>
  <v-row v-if="!isLoading" class="pb-6 pt-4">
    <v-col cols="12">
      <MaterialResults
        :is-loading="isLoading"
        :deduplicated-materials="deduplicatedMaterials"
        :has-more="hasMore"
        source="video_related_materials"
        @load-more="next"
      ></MaterialResults>
    </v-col>
  </v-row>
  <v-row v-else>
    <v-col v-for="index in 4" :key="index">
      <v-skeleton-loader height="250px" type="ossein"></v-skeleton-loader>
    </v-col>
  </v-row>
</template>

<style scoped lang="scss">
:deep(.material-ttl) {
  font-size: 0.875rem;
}
</style>
